import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import { Project } from 'src/model';
import { requiredDeep, snakeToCamelDeep } from 'src/utils/object';

import { getCurrentProjectId, setCurrentProjectId, storedCurrentProjectId } from '../current-project-id';
import { apiQuery } from '../query';

export { getCurrentProjectId, setCurrentProjectId };

export function useProjectsQuery({ search, limit }: Partial<{ search: string; limit: number }> = {}) {
  return useQuery({
    ...apiQuery('get /v1/projects', {
      query: {
        name: search || undefined,
        limit: limit ? String(limit) : undefined,
      },
    }),
    placeholderData: keepPreviousData,
    select: ({ projects }): Project[] => projects!.map((project) => snakeToCamelDeep(requiredDeep(project))),
  });
}

export function useProjects(...params: Parameters<typeof useProjectsQuery>) {
  return useProjectsQuery(...params).data ?? [];
}

export function useProjectQuery(projectId?: string) {
  return useQuery({
    ...apiQuery('get /v1/projects/{id}', { path: { id: projectId! } }),
    enabled: projectId !== undefined,
    select: ({ project }): Project => snakeToCamelDeep(requiredDeep(project!)),
  });
}

export function useProject(projectId?: string) {
  return useProjectQuery(projectId).data;
}

export function useCurrentProjectId(): [string | undefined, (projectId: string) => void] {
  const projectId = useSyncExternalStore(storedCurrentProjectId.listen, storedCurrentProjectId.read);

  return [projectId ?? undefined, storedCurrentProjectId.write];
}

export function useCurrentProjectQuery() {
  const [projectId] = useCurrentProjectId();

  return useQuery({
    ...apiQuery('get /v1/projects/{id}', {
      path: { id: projectId! },
    }),
    enabled: projectId !== undefined,
    select: ({ project }) => snakeToCamelDeep(requiredDeep(project)),
  });
}

export function useCurrentProject() {
  return useCurrentProjectQuery().data;
}
